<template>
  <UDashboardPanel id="workflows" :ui="{ body: 'h-full w-full sm:p-0 p-0' }">
    <!--
    <template #header>
      <UDashboardNavbar title="Workflow">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>
        <template #default>
          <AssignedFilter v-if="[Permission.VehicleSupplier, Permission.SupplierSuperUser, Permission.TyreSupplier, Permission.CustomerCare, Permission.Driver, Permission.HR, Permission.FleetManager, Permission.KeyAccountManager, Permission.ClientAccount, Permission.Backoffice, Permission.DeliveryManager, Permission.DeliverySpecialist, Permission.PurchaseSupport, Permission.Dealer].some(p => userStore.hasPermission(p))" :assigned="filterValues.assigned" @filter-changed="handleAssignedFilterChange" />
          <ViewSwitcher v-if="[Permission.KeyAccountManager, Permission.ClientAccount, Permission.Backoffice, Permission.DeliveryManager, Permission.DeliverySpecialist, Permission.PurchaseSupport, Permission.ITAdmin].some(p => userStore.hasPermission(p))" />
        </template>
        <template #right>
          <LanguageSwitcher />
        </template>
      </UDashboardNavbar>
    </template>
    -->
    
    <template #body>
      <div class="w-full h-full flex flex-col">
        <div class="flex-1 flex flex-col pt-4 pb-[115px] lg:pb-0 overflow-hidden">
          <div class="flex-1 min-h-0">
            <UTable
              ref="table"
              :key="viewStore.currentView"
              :columns="columns"
              :data="status === 'pending' ? skeletonData : groupedData"
              sticky
              @hover="() => {}"
              class="w-full h-full px-4"
              :meta="{ class: { tr: (row) => row.original && row.original._isChild ? 'bg-gray-100 dark:bg-gray-800' : (row.original && row.original._isGroup && row.original._purchaseOrderCount > 1 ? 'bg-gray-50/50 dark:bg-gray-800/50' : '') } }"
              :ui="{
                base: 'table-fixed border-separate border-spacing-0',
                thead: '[&>tr]:bg-(--ui-bg-elevated)/50 [&>tr]:after:content-none',
                tbody: '[&>tr]:last:[&>td]:border-b-0',
                th: 'py-2 align-top first:rounded-l-[calc(var(--ui-radius)*2)] last:rounded-r-[calc(var(--ui-radius)*2)] border-y border-(--ui-border) first:border-l last:border-r',
                td: 'border-b border-(--ui-border)'
              }"
            />
          </div>
        </div>

        <div class="fixed bottom-0 left-0 right-0 lg:sticky bg-(--ui-bg) border-t border-(--ui-border) px-4 py-3.5 z-10">
          <div class="flex items-center justify-between gap-3 min-h-8">
            <div class="text-sm text-(--ui-text-muted)">
              <span v-if="pagination">
                <span class="hidden lg:inline">{{ t('workflows.table.showing') }}</span> {{ ((pagination.currentPage - 1) * pagination.pageSize) + 1 }} - {{ Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems) }} <span class="hidden lg:inline">{{ t('workflows.table.of') }}</span><span class="lg:hidden">/</span> {{ pagination.totalItems }}
              </span>
              <span v-else>{{ t('workflows.table.loading') }}</span>
            </div>

            <div class="flex items-center gap-3">
              <USelect
                v-model="selectedPageSize"
                :items="pageSizeOptions"
                size="sm"
                class="w-20 cursor-pointer"
                @update:model-value="handlePageSizeChange"
              />
              <UPagination
                v-if="pagination && pagination.totalItems > pagination.pageSize"
                v-model:page="currentPaginationPage"
                :total="pagination.totalItems"
                :items-per-page="pagination.pageSize"
                :sibling-count="1"
                size="sm"
                class="lg:hidden"
                :ui="{
                  item: 'cursor-pointer',
                  prev: 'cursor-pointer',
                  next: 'cursor-pointer',
                  first: 'hidden',
                  last: 'hidden'
                }"
              />
              <UPagination
                v-if="pagination && pagination.totalItems > pagination.pageSize"
                v-model:page="currentPaginationPage"
                :total="pagination.totalItems"
                :items-per-page="pagination.pageSize"
                :sibling-count="2"
                size="sm"
                class="hidden lg:flex"
                :ui="{
                  item: 'cursor-pointer',
                  prev: 'cursor-pointer',
                  next: 'cursor-pointer',
                  first: 'cursor-pointer',
                  last: 'cursor-pointer'
                }"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </UDashboardPanel>

  <WorkflowDetailModal
    v-if="isModalOpen && selectedExternalReference"
    v-model="isModalOpen"
    :external-reference="selectedExternalReference"
    :purchase-order-type="selectedPurchaseOrderType"
    @closeModal="closeModal"
  />
</template>

<script setup lang="ts">
import { computed, h, nextTick, onMounted, ref, resolveComponent, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useWorkflowsData } from '@/composables/useWorkflowsData'
import { useI18n } from "vue-i18n";
import { debounce, showError, showSuccess } from '@/utils/utils.ts'
import { useViewStore } from "@/store/ViewStore.ts";
import { CalendarDate, DateFormatter, getLocalTimeZone, today } from '@internationalized/date'
import { useUser } from "@/store";
import { Permission } from "@/types/enums/Permission.ts";
import { Tenant } from "@/types/enums/Tenant.ts";
import { getSapLocalizedText } from "@/utils/sapLocalization";
import { PurchaseOrder, isVehicleOrderType } from "@/types/enums/PurchaseOrder.ts";
import { useConfig, usePreferences } from "@/store";
import { useWorkflowEdit } from "@/store/workflowEdit.ts";
import { WorkflowStatus, QuotationStep } from '@/api/eOrdersApi'
import { formatDate } from "@/utils/date.ts";
import WorkflowDetailModal from '@/components/workflows/WorkflowDetailModal.vue'
import AssignedFilter from '@/components/workflows/AssignedFilter.vue'
import ViewSwitcher from '@/components/ViewSwitcher'

const UButton = resolveComponent('UButton')
const UBadge = resolveComponent('UBadge')
const UInput = resolveComponent('UInput')
const USelect = resolveComponent('USelect')
const UIcon = resolveComponent('UIcon')
const UCalendar = resolveComponent('UCalendar')
const UPopover = resolveComponent('UPopover')
const USeparator = resolveComponent('USeparator')
const UTooltip = resolveComponent('UTooltip')

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const userStore = useUser();
const preferences = usePreferences()
const workflowEditStore = useWorkflowEdit();

const viewStore = useViewStore();

const { data, status, pagination, fetchWorkflows, applyFilters, clearFilters, goToPage, changePageSize, error, currentPage, pageSize, currentFilters } = useWorkflowsData()

const df = new DateFormatter('cs-CZ', {
  dateStyle: 'short'
})

const expandedRows = ref<Set<string>>(new Set())

const groupedData = computed(() => {
  const result: any[] = []

  data.value.forEach(row => {
    if (!row.purchaseOrders || row.purchaseOrders.length <= 1) {
      result.push({
        ...row,
        _isGroup: false,
        _currentPurchaseOrder: row.purchaseOrders?.[0] || null
      })
    } else {
      const isExpanded = expandedRows.value.has(row.externalReference)
      const vehicleOrder = row.purchaseOrders.find(po => isVehicleOrderType(po.type))
      const primaryPO = vehicleOrder || row.purchaseOrders[0]
      
      const groupRow = {
        ...row,
        _isGroup: true,
        _isExpanded: isExpanded,
        _purchaseOrderCount: row.purchaseOrders.length,
        step: row.step,
        supplier: primaryPO.supplier,
      }
      
      result.push(groupRow)
      
      if (isExpanded) {
        row.purchaseOrders.forEach((purchaseOrder, index) => {
          result.push({
            ...row,
            ...purchaseOrder,
            _isChild: true,
            _childIndex: index,
            _parentWorkflow: row
          })
        })
      }
    }
  })

  return result
})

const toggleRowExpansion = (externalReference: string) => {
  if (expandedRows.value.has(externalReference)) {
    expandedRows.value.delete(externalReference)
  } else {
    expandedRows.value.add(externalReference)
  }
}

const initializeExpandedRows = () => {
  const newExpandedRows = new Set<string>()
  
  data.value.forEach(row => {
    if (row.purchaseOrders && row.purchaseOrders.length > 1) {
      newExpandedRows.add(row.externalReference)
    }
  })
  
  expandedRows.value = newExpandedRows
}

const selectedPageSize = ref(preferences.workflowsPageSize)
const pageSizeOptions = [
  { label: '20', value: 20 },
  { label: '40', value: 40 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
]

const currentPaginationPage = ref(1)

const skeletonData = computed(() => {
  if (status.value !== 'pending') return []

  return Array.from({ length: 10 }, (_, index) => ({
    id: `skeleton-${index}`,
    externalReference: '',
    manufacturer: '',
    model: '',
    status: '',
    expectedDeliveryDate: '',
    assignees: [],
    supplier: null,
    client: { name: '', id: '' },
    _skeleton: true
  }))
})


const isModalOpen = ref(false)
const selectedExternalReference = ref<string | null>(null)
const selectedPurchaseOrderType = ref<string | undefined>(undefined)

const table = useTemplateRef('table')

const columnFilters = ref([])

const filterValues = ref({
  externalReference: '',
  client: '',
  description: '',
  supplierName: '',
  vehicle: '',
  status: [],
  statusStep: [],
  assignee: '',
  assigned: route.query.assigned === 'true',
  workflowDate: '',
  dateFilter: 'all',
  dateRange: null as DateRange | null,
  dateFilterType: 'all' as 'custom' | 'today' | 'week' | 'month' | 'year' | 'all'
})

interface DateRange {
  start: CalendarDate
  end: CalendarDate
}
const getDateRangeFromFilterType = (type: string): DateRange | null => {
  const timezone = getLocalTimeZone()
  const todayDate = today(timezone)

  switch (type) {
    case 'today':
      return {
        start: todayDate,
        end: todayDate
      }
    case 'week':
      const dayOfWeek = todayDate.day
      const monday = todayDate.subtract({ days: dayOfWeek - 1 })
      const sunday = monday.add({ days: 6 })
      return {
        start: monday,
        end: sunday
      }
    case 'month':
      const monthStart = todayDate.set({ day: 1 })
      const monthEnd = monthStart.add({ months: 1 }).subtract({ days: 1 })
      return {
        start: monthStart,
        end: monthEnd
      }
    case 'year':
      const yearStart = todayDate.set({ month: 1, day: 1 })
      const yearEnd = todayDate.set({ month: 12, day: 31 })
      return {
        start: yearStart,
        end: yearEnd
      }
    default:
      return null
  }
}

const dateFilterOptions = computed(() => [
  { label: t('workflows.table.dateFilters.allDates'), value: 'all' },
  { label: t('workflows.table.dateFilters.today'), value: 'today' },
  { label: t('workflows.table.dateFilters.thisWeek'), value: 'week' },
  { label: t('workflows.table.dateFilters.thisMonth'), value: 'month' },
  { label: t('workflows.table.dateFilters.thisYear'), value: 'year' },
  { label: t('workflows.table.dateFilters.customRange'), value: 'custom' }
])

const hasActiveFilters = computed(() => {
  return filterValues.value.externalReference !== '' ||
    filterValues.value.client !== '' ||
    filterValues.value.description !== '' ||
    filterValues.value.supplierName !== '' ||
    filterValues.value.vehicle !== '' ||
    filterValues.value.status.length > 0 ||
    filterValues.value.statusStep.length > 0 ||
    filterValues.value.assignee !== '' ||
    filterValues.value.dateFilterType !== 'all'
})

const resetFilters = () => {
  filterValues.value = {
    externalReference: '',
    client: '',
    description: '',
    supplierName: '',
    vehicle: '',
    status: [],
    statusStep: [],
    assignee: '',
    assigned: filterValues.value.assigned,
    workflowDate: '',
    dateFilter: 'all',
    dateRange: null,
    dateFilterType: 'all'
  }
}

const handleDateFilterTypeChange = (type: string) => {
  filterValues.value.dateFilterType = type as typeof filterValues.value.dateFilterType

  if (type === 'custom') {
    return
  } else if (type === 'all') {
    filterValues.value.dateRange = null
  } else {
    filterValues.value.dateRange = getDateRangeFromFilterType(type)
  }
}
const getDateFilterDisplayText = (): string => {
  const dateFilterType = filterValues.value.dateFilterType
  const dateRange = filterValues.value.dateRange

  if (dateFilterType === 'all') {
    return t('workflows.table.dateFilters.allDates')
  }

  if (dateRange?.start) {
    const start = df.format(dateRange.start.toDate(getLocalTimeZone()))
    const end = dateRange.end ? df.format(dateRange.end.toDate(getLocalTimeZone())) : ''
    return end ? `${start} - ${end}` : `${start} -`
  }

  const option = dateFilterOptions.value.find(opt => opt.value === dateFilterType)
  return option?.label || t('workflows.table.filters.dateRange')
}

const scrollTableToTop = () => {
  nextTick(() => {
    const el = table.value?.$el
    
    if (el) el.scrollTop = 0
  })
}

const handlePageChange = async (page: number) => {
  try {
    if (pagination.value && page <= pagination.value.totalPages && page > 0) {
      await goToPage(page)
      scrollTableToTop()
    } else {
      showError(error instanceof Error ? error.message : String(error))
    }
  } catch (error) {
    showError(error instanceof Error ? error.message : String(error))
  }
}

const handlePageSizeChange = async (newSize: number) => {
  try {
    await changePageSize(newSize)
    selectedPageSize.value = newSize
    currentPaginationPage.value = 1
    preferences.setWorkflowsPageSize(newSize)
    scrollTableToTop()
  } catch (error) {
    showError(error instanceof Error ? error.message : String(error))
  }
}

watch(pagination, (newPagination) => {
  if (newPagination && newPagination.currentPage !== currentPaginationPage.value) {
    currentPaginationPage.value = newPagination.currentPage
  }
}, { deep: true })

watch(currentPaginationPage, async (newPage) => {
  if (newPage !== pagination.value?.currentPage) {
    await handlePageChange(newPage)
  }
})

const showDetails = (row, poType?: string) => {
  const vehicleOrder = row?.purchaseOrders?.find(po => isVehicleOrderType(po.type));
  const resolvedType = poType ?? vehicleOrder?.type ?? row?.purchaseOrders?.[0]?.type ?? undefined
  
  selectedExternalReference.value = row.externalReference
  selectedPurchaseOrderType.value = resolvedType
  isModalOpen.value = true
  
  router.push({
    query: {
      ...route.query,
      modalExternalReference: row.externalReference.toString(),
      ...(resolvedType ? { modalType: resolvedType.toString() } : {}),
    },
  })
}

const closeModal = () => {
  setTimeout(async () => {
    isModalOpen.value = false
    selectedExternalReference.value = null
    selectedPurchaseOrderType.value = undefined
    
    workflowEditStore.setEditMode(false);
    workflowEditStore.setEditableWorkflow(null);
    
    const query = {...route.query}
    delete query.modalExternalReference
    delete query.modalType
    delete query.tab
    router.replace({ query })
    
    await fetchWorkflows(selectedPageSize.value, currentPaginationPage.value, currentFilters.value, true)
  }, 100)
}

const columns = computed(() => {
  const configStore = useConfig();
  const tenant = computed(() => configStore.configuration.tenant);

  const filterTrailingSlot = (key: 'externalReference' | 'client' | 'description' | 'supplierName' | 'vehicle') =>
    filterValues.value[key]
      ? {
          trailing: () => h(UButton, {
            color: 'neutral',
            variant: 'link',
            size: 'xs',
            icon: 'i-lucide-circle-x',
            'aria-label': t('workflows.table.filters.clear'),
            onClick: () => { filterValues.value[key] = '' }
          })
        }
      : undefined

  const statusFilterOptions = computed(() => {
    const options = [
    {
      label: t('processStatus.categories.statusWithStep'),
      type: 'label',
      class: 'font-semibold text-gray-400 text-xs uppercase tracking-wide px-2 py-1'
    },
    // QuotationApproval
    {
      label: t('processStatus.combined.quotationParked'),
      value: 'status-step:QuotationApproval:Parked',
      icon: 'i-mdi-pause-circle-outline',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    {
      label: t('processStatus.combined.quotationSchemaFinalization'),
      value: 'status-step:QuotationApproval:SchemaFinalization',
      icon: 'i-mdi-file-document-edit',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    {
      label: t('processStatus.combined.quotationApprovals'),
      value: 'status-step:QuotationApproval:Approvals',
      icon: 'i-mdi-file-document-edit',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    {
      label: t('processStatus.combined.quotationClientApproval'),
      value: 'status-step:QuotationApproval:ClientApproval',
      icon: 'i-mdi-file-document-edit',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ]
    },
    {
      label: t('processStatus.combined.quotationClientAccountAcceptation'),
      value: 'status-step:QuotationApproval:ClientAccountAcceptation',
      icon: 'i-mdi-file-document-edit',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ]
    },
    {
      label: t('processStatus.combined.quotationOrderCreation'),
      value: 'status-step:QuotationApproval:OrderCreation',
      icon: 'i-mdi-file-document-edit',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    {
      label: '',
      type: 'label',
      class: 'border-t border-gray-200 dark:border-gray-700 mt-1 pb-0'
    },
    // OrderApproval
    {
      label: t('processStatus.combined.orderKamApproval'),
      value: 'status-step:OrderApproval:KamApproval',
      icon: 'i-mdi-file-check',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ]
    },
    {
      label: t('processStatus.combined.orderOdApproval'),
      value: 'status-step:OrderApproval:ODApproval',
      icon: 'i-mdi-file-check',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ]
    },
    {
      label: t('processStatus.combined.orderDealerAcceptation'),
      value: 'status-step:OrderApproval:DealerAcceptation',
      icon: 'i-mdi-file-check',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ]
    },
    {
      label: t('processStatus.combined.orderDealerConfirmation'),
      value: 'status-step:OrderApproval:DealerConfirmation',
      icon: 'i-mdi-file-check',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    /*
    {
      label: t('processStatus.combined.orderApprovalParked'),
      value: 'status-step:OrderApproval:Parked',
      icon: 'i-mdi-pause-circle-outline',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    */
    {
      label: '',
      type: 'label',
      class: 'border-t border-gray-200 dark:border-gray-700 mt-1 pb-0'
    },
    // OrderProcessing, OrderUpdated
    {
      label: t('processStatus.combined.orderProcessingWaiting'),
      value: 'status-step:OrderProcessing:Waiting',
      icon: 'i-mdi-cog-transfer',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    {
      label: t('processStatus.combined.orderUpdatedWaiting'),
      value: 'status-step:OrderUpdated:Waiting',
      icon: 'i-mdi-cog-transfer',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    /*
    {
      label: t('processStatus.combined.orderProcessingParked'),
      value: 'status-step:OrderProcessing:Parked',
      icon: 'i-mdi-pause-circle-outline',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    {
      label: t('processStatus.combined.orderUpdatedParked'),
      value: 'status-step:OrderUpdated:Parked',
      icon: 'i-mdi-pause-circle-outline',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    */
    {
      label: '',
      type: 'label',
      class: 'border-t border-gray-200 dark:border-gray-700 mt-1 pb-0'
    },
    // Delivery
    {
      label: t('processStatus.combined.deliveryVehicleRegistration'),
      value: 'status-step:Delivery:VehicleRegistration',
      icon: 'i-mdi-truck-delivery',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ]
    },
    {
      label: t('processStatus.combined.deliveryHandoverPreparation'),
      value: 'status-step:Delivery:HandoverPreparation',
      icon: 'i-mdi-truck-delivery',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ]
    },
    {
      label: t('processStatus.combined.deliveryHandover'),
      value: 'status-step:Delivery:Handover',
      icon: 'i-mdi-truck-delivery',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    /*
    {
      label: t('processStatus.combined.deliveryParked'),
      value: 'status-step:Delivery:Parked',
      icon: 'i-mdi-pause-circle-outline',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    */
    {
      label: '',
      type: 'label',
      class: 'border-t border-gray-200 dark:border-gray-700 mt-1 pb-0'
    },
    // TyreOrder
    {
      label: t('processStatus.combined.tyreSupplierApproval'),
      value: 'status-step:OrderApproval:SupplierApproval',
      icon: 'i-mdi-tire',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    {
      label: t('processStatus.combined.tyreSupplierAcceptation'),
      value: 'status-step:OrderApproval:SupplierAcceptation',
      icon: 'i-mdi-tire',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    {
      label: t('processStatus.combined.tyreDeliveryConfirmation'),
      value: 'status-step:Delivery:DeliveryConfirmation',
      icon: 'i-mdi-tire',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    {
      label: t('processStatus.combined.tyreDelivered'),
      value: 'status-step:Delivery:Delivered',
      icon: 'i-mdi-tire',
      class: 'cursor-pointer',
      tenants: [Tenant.FI]
    },
    {
      label: '',
      type: 'label',
      class: 'border-t border-gray-200 dark:border-gray-700 mt-1 pb-0'
    },
    /*
    {
      label: t('processStatus.categories.statusOnly'),
      type: 'label',
      class: 'font-semibold text-gray-400 text-xs uppercase tracking-wide px-2 py-1'
    },
    {
      label: t('processStatus.status.quotationApproval'),
      value: 'status:QuotationApproval',
      icon: 'i-mdi-file-document-edit',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    */
    {
      label: t('processStatus.status.quotationRejected'),
      value: 'status:QuotationRejected',
      icon: 'i-mdi-close-circle',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    /*
    {
      label: t('processStatus.status.orderApproval'),
      value: 'status:OrderApproval',
      icon: 'i-mdi-file-check',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    */
    {
      label: t('processStatus.status.orderRejected'),
      value: 'status:OrderRejected',
      icon: 'i-mdi-close-circle',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    /*
    {
      label: t('processStatus.status.orderProcessing'),
      value: 'status:OrderProcessing',
      icon: 'i-mdi-cog-transfer',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    {
      label: t('processStatus.status.orderUpdated'),
      value: 'status:OrderUpdated',
      icon: 'i-mdi-cog-transfer',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    {
      label: t('processStatus.status.delivery'),
      value: 'status:Delivery',
      icon: 'i-mdi-truck-delivery',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    */
    {
      label: t('processStatus.status.completed'),
      value: 'status:Completed',
      icon: 'i-mdi-check-circle',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    },
    /*
    {
      label: t('processStatus.status.archived'),
      value: 'status:Archived',
      icon: 'i-mdi-archive',
      class: 'cursor-pointer',
      tenants: [Tenant.CZ, Tenant.FI]
    }
    */
  ];

  return options.filter(o => !o.tenants || o.tenants.includes(tenant.value as Tenant));
});
  
  const baseColumns = [
    {
      id: 'actions',
      header: () => {
        return h('div', { class: 'flex flex-col gap-1.5 items-center justify-center w-[100px]' }, [
          h('span', { class: 'text-[11px] font-semibold text-transparent uppercase tracking-wider' }, '.'),
          h(UButton, {
            icon: 'i-heroicons-trash',
            label: t('workflows.table.filters.clear'),
            color: 'error',
            variant: 'ghost',
            size: 'xs',
            class: ['transition-all duration-200 px-2 py-1 gap-1.5 font-medium hover:bg-red-50 dark:hover:bg-red-900/20', hasActiveFilters.value ? 'opacity-100 scale-100 cursor-pointer' : 'opacity-0 scale-95 pointer-events-none'],
            onClick: resetFilters
          })
        ])
      },
      cell: ({ row }) => {
        if (row.original._skeleton) {
          return h('div', { class: 'flex gap-2 justify-center w-[100px]' }, [
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-6 w-6 rounded' })
          ])
        }
        
        const isGroup = row.original._isGroup
        const isChild = row.original._isChild
        const hasMultiplePurchaseOrders = row.original._purchaseOrderCount > 1

        return h('div', {
          class: `text-left flex items-center gap-1 justify-center w-[100px]`
        }, [
          isGroup && hasMultiplePurchaseOrders ? h(UButton, {
            color: 'neutral',
            variant: 'solid',
            icon: row.original._isExpanded ? 'i-heroicons-chevron-down' : 'i-heroicons-chevron-right',
            class: 'bg-gray-600 hover:bg-gray-700 cursor-pointer text-white transition-all duration-200',
            size: 'sm',
            onClick: () => toggleRowExpansion(row.original.externalReference)
          }) : null,
            
          /*
          isChild ? h('div', {
            class: 'border-l-[2px] border-b-[2px] border-gray-500 dark:border-gray-400 w-5 h-5 rounded-bl-md mr-1 ml-11 self-center mb-2'
          }) : null,
          */
            
          (!isGroup || isChild) ? h(UButton, {
            color: [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(row.original.status) || (row.original._isChild ? row.original._parentWorkflow : row.original)?.quotation?.currentProcessStep === QuotationStep.Parked ? 'info' : 'primary',
            variant: 'solid',
            icon: 'i-heroicons-magnifying-glass',
            class: ['hover:scale-105 cursor-pointer text-white transition-transform duration-200', isChild ? 'ml-[38px]' : ''],
            size: 'sm',
            onClick: () => {
              const vehicleOrder = row.original?.purchaseOrders?.find(po => isVehicleOrderType(po.type));
              showDetails(row.original, row.original._isChild ? row.original.type : vehicleOrder?.type ?? row.original?.purchaseOrders?.[0]?.type)
            }
          }) : null
        ])
      }
    },
    {
      accessorKey: 'externalReference',
      header: () => {
        const isActive = !!filterValues.value.externalReference
        return h('div', { class: 'flex flex-col gap-1.5 w-[120px]' }, [
          h('span', { 
            class: ['text-[11px] font-semibold uppercase tracking-wider ml-1.5 transition-colors duration-200', isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400']
          }, t('workflows.table.columns.externalReference')),
          h(UInput, {
            modelValue: filterValues.value.externalReference,
            'onUpdate:modelValue': (value) => {
              filterValues.value.externalReference = value || ''
            },
            placeholder: t('workflows.table.filters.externalReference'),
            size: 'sm',
            color: isActive ? 'primary' : 'neutral',
            class: 'w-full',
            ui: {
              base: ['transition-all duration-300 shadow-none border-none focus:ring-2 focus:ring-primary-500/50', isActive ? 'ring-2 ring-primary-500/70 bg-primary-50/50 dark:bg-primary-900/30' : 'bg-(--ui-bg) ring-1 ring-(--ui-border) shadow-none'],
              trailing: 'pe-1'
            }
          }, filterTrailingSlot('externalReference'))
        ])
      },
      cell: ({ row }) => {
        if (row.original._skeleton) {
          return h('div', {
            class: 'h-full -m-4 flex flex-col justify-center px-4 py-4 gap-2 w-[120px]'
          }, [
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-24' }),
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-5 rounded w-16' })
          ])
        }
        
        const isGroup = row.original._isGroup
        const isChild = row.original._isChild
        
        return h('div', {
          class: `text-sm font-semibold cursor-pointer h-full -m-4 flex flex-col justify-center px-4 py-4 w-[120px] relative ${
            isGroup ? 'text-(--ui-text)' : isChild ? 'text-(--ui-text)' : 'text-(--ui-text)'
          }`,
          onDblclick: () => {
            const vehicleOrder = row.original?.purchaseOrders?.find(po => isVehicleOrderType(po.type));
            showDetails(row.original, row.original._isChild ? row.original.type : vehicleOrder?.type ?? row.original?.purchaseOrders?.[0]?.type)
          }
        }, [
          isChild ? h('div', {
            class: `inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold w-fit ${
              isVehicleOrderType(row.original.type) ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-300 dark:ring-indigo-700' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 ring-1 ring-amber-300 dark:ring-amber-700'
            }`
          }, [
            h(UIcon, {
              name: isVehicleOrderType(row.original.type) ? 'i-mdi-car-hatchback' : 'i-mdi-tire',
              class: 'w-3.5 h-3.5'
            }),
            h('span', isVehicleOrderType(row.original.type) ? t('workflowDetail.purchaseOrderType.vehicle') : t('workflowDetail.purchaseOrderType.tyre'))
          ]) : null,
            
          !isChild ? h('span', row.original.externalReference) : null,
            
          !isGroup && !isChild && row.original.purchaseOrders?.length === 1 ? h('div', {
            class: `inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold w-fit ${
              isVehicleOrderType(row.original.purchaseOrders[0]?.type) ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-300 dark:ring-indigo-700' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 ring-1 ring-amber-300 dark:ring-amber-700'
            }`
          }, [
            h(UIcon, {
              name: isVehicleOrderType(row.original.purchaseOrders[0]?.type) ? 'i-mdi-car-hatchback' : 'i-mdi-tire',
              class: 'w-3.5 h-3.5'
            }),
            h('span', isVehicleOrderType(row.original.purchaseOrders[0]?.type) ? t('workflowDetail.purchaseOrderType.vehicle') : t('workflowDetail.purchaseOrderType.tyre'))
          ]) : null
        ])
      }
    },
    {
      accessorKey: 'status',
      header: () => {
        const isActive = filterValues.value.statusStep && filterValues.value.statusStep.length > 0
        return h('div', { class: 'flex flex-col gap-1.5 w-[180px]' }, [
          h('span', { 
            class: ['text-[11px] font-semibold uppercase tracking-wider ml-1.5 transition-colors duration-200', isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400']
          }, t('workflows.table.columns.status')),
          h(USelect, {
            modelValue: filterValues.value.statusStep,
            'onUpdate:modelValue': (value) => {
              filterValues.value.statusStep = value || []
            },
            items: statusFilterOptions.value,
            multiple: true,
            placeholder: t('workflows.table.filters.status'),
            size: 'sm',
            color: isActive ? 'primary' : 'neutral',
            class: 'w-[180px]',
            ui: {
              base: ['transition-all duration-300 shadow-none border-none focus:ring-2 focus:ring-primary-500/50', isActive ? 'ring-2 ring-primary-500/70 bg-primary-50/50 dark:bg-primary-900/30' : 'bg-(--ui-bg) ring-1 ring-(--ui-border) shadow-none'],
              content: 'max-h-full w-full overflow-y-auto'
            }
          })
        ])
      },
      cell: ({ row }) => {
        if (row.original._skeleton) {
          return h('div', { class: 'flex items-center gap-2 w-[180px]' }, [
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) w-8 h-8 rounded-lg flex-shrink-0' }),
            h('div', { class: 'space-y-1' }, [
              h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-24' }),
              h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-3 rounded w-32' })
            ])
          ])
        }
        
        const isGroup = row.original._isGroup
        const isChild = row.original._isChild
        
        const statusConfig = {
          [WorkflowStatus.QuotationApproval]: {
            color: 'orange',
            bgColor: 'bg-orange-100 dark:bg-orange-900/30',
            textColor: 'text-orange-600 dark:text-orange-400',
            ringColor: 'ring-orange-300 dark:ring-orange-700',
            icon: 'i-mdi-file-document-edit',
            label: t('processStatus.status.quotationApproval')
          },
          [WorkflowStatus.OrderApproval]: {
            color: 'blue',
            bgColor: 'bg-blue-100 dark:bg-blue-900/30',
            textColor: 'text-blue-600 dark:text-blue-400',
            ringColor: 'ring-blue-300 dark:ring-blue-700',
            icon: 'i-mdi-file-check',
            label: t('processStatus.status.orderApproval')
          },
          [WorkflowStatus.OrderProcessing]: {
            color: 'purple',
            bgColor: 'bg-purple-100 dark:bg-purple-900/30',
            textColor: 'text-purple-600 dark:text-purple-400',
            ringColor: 'ring-purple-300 dark:ring-purple-700',
            icon: 'i-mdi-cog-transfer',
            label: t('processStatus.status.orderProcessing')
          },
          [WorkflowStatus.OrderUpdated]: {
            color: 'purple',
            bgColor: 'bg-purple-100 dark:bg-purple-900/30',
            textColor: 'text-purple-600 dark:text-purple-400',
            ringColor: 'ring-purple-300 dark:ring-purple-700',
            icon: 'i-mdi-cog-transfer',
            label: t('processStatus.status.orderProcessing')
          },
          [WorkflowStatus.Delivery]: {
            color: 'orange',
            bgColor: 'bg-orange-100 dark:bg-orange-900/30',
            textColor: 'text-orange-600 dark:text-orange-400',
            ringColor: 'ring-orange-300 dark:ring-orange-700',
            icon: 'i-mdi-truck-delivery',
            label: t('processStatus.status.delivery')
          },
          [WorkflowStatus.QuotationRejected]: {
            color: 'red',
            bgColor: 'bg-red-100 dark:bg-red-900/30',
            textColor: 'text-red-600 dark:text-red-400',
            ringColor: 'ring-red-300 dark:ring-red-700',
            icon: 'i-mdi-close-circle',
            label: t('processStatus.status.quotationRejected')
          },
          [WorkflowStatus.OrderRejected]: {
            color: 'red',
            bgColor: 'bg-red-100 dark:bg-red-900/30',
            textColor: 'text-red-600 dark:text-red-400',
            ringColor: 'ring-red-300 dark:ring-red-700',
            icon: 'i-mdi-close-circle',
            label: t('processStatus.status.orderRejected')
          },
          [WorkflowStatus.Completed]: {
            color: 'green',
            bgColor: 'bg-green-100 dark:bg-green-900/30',
            textColor: 'text-green-600 dark:text-green-400',
            ringColor: 'ring-green-300 dark:ring-green-700',
            icon: 'i-mdi-check-circle',
            label: t('processStatus.status.completed')
          },
          [WorkflowStatus.Archived]: {
            color: 'gray',
            bgColor: 'bg-(--ui-bg-elevated)',
            textColor: 'text-gray-600 dark:text-gray-400',
            ringColor: 'ring-gray-300 dark:ring-gray-600',
            icon: 'i-mdi-archive',
            label: t('processStatus.status.archived')
          }
        }
        
        /*
        if (isGroup && row.original._purchaseOrderCount > 1) {
          const allStatuses = row.original.purchaseOrders.map(po => po.currentProcessStep)
          const uniqueStatuses = [...new Set(allStatuses)]

          if (uniqueStatuses.length > 1) {
            return h('div', { class: 'flex items-center gap-2' }, [
              h('div', {
                class: 'w-8 h-8 bg-gradient-to-r from-blue-100 to-orange-100 rounded-lg flex items-center justify-center flex-shrink-0'
              }, [
                h(UIcon, {
                  name: 'i-heroicons-squares-2x2',
                  class: 'text-gray-600 text-sm'
                })
              ]),
              h('div', { class: 'min-w-0' }, [
                h('div', { class: 'text-sm font-medium text-gray-900 dark:text-gray-100 truncate' }, 'Mixed Status'),
                h('div', { class: 'text-sm text-gray-500 truncate' }, `${uniqueStatuses.length} different`)
              ])
            ])
          }
        }
        */

        const status = row.getValue('status')
        if (!status) return null

        const config = statusConfig[status as keyof typeof statusConfig]

        return h('div', {
          class: 'flex items-center gap-2 w-[180px]'
        }, [
          h('div', {
            class: `w-8 h-8 ${config.bgColor} rounded-lg flex items-center justify-center flex-shrink-0 ring-1 ${config.ringColor}`
          }, [
            h(UIcon, {
              name: config.icon,
              class: `${config.textColor} text-sm`
            })
          ]),
          h('div', { class: 'min-w-0' }, [
            h('div', {
              class: 'text-sm font-medium truncate text-gray-900 dark:text-gray-100'
            }, config.label),
            (() => {
              const parentWorkflow = row.original._isChild ? row.original._parentWorkflow : row.original;
              const workflowStatus = parentWorkflow?.status;
              
              if (!workflowStatus) return null;
              
              let step: string | null = null;
              const isTyreOrder = row.original._isChild && row.original.type === PurchaseOrder.Tyre;
              const isSingleTyreOrder = !row.original._isGroup && !row.original._isChild && row.original._currentPurchaseOrder?.type === PurchaseOrder.Tyre;
              
              if (isTyreOrder && row.original.currentProcessStep) {
                step = row.original.currentProcessStep;
              } else if (isSingleTyreOrder && row.original._currentPurchaseOrder?.currentProcessStep) {
                step = row.original._currentPurchaseOrder.currentProcessStep;
              } else {
                if ([WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflowStatus)) {
                  step = parentWorkflow?.quotation?.currentProcessStep ?? null;
                } else if ([WorkflowStatus.OrderApproval, WorkflowStatus.OrderRejected].includes(workflowStatus)) {
                  step = parentWorkflow?.step ?? null;
                } else if ([WorkflowStatus.OrderProcessing, WorkflowStatus.OrderUpdated, WorkflowStatus.Delivery, WorkflowStatus.Completed, WorkflowStatus.Archived].includes(workflowStatus)) {
                  step = parentWorkflow?.delivery?.currentProcessStep ?? null;
                } else {
                  step = parentWorkflow?.step ?? null;
                }
              }
              
              if (!step) return null;
              
              const statusKey = workflowStatus.charAt(0).toLowerCase() + workflowStatus.slice(1);
              const stepKey = String(step).charAt(0).toLowerCase() + String(step).slice(1);
              const translationKey = `processStatus.step.${statusKey}.${stepKey}`;
              
              const translated = t(translationKey);
              
              if (isTyreOrder && translated === translationKey && statusKey !== 'orderApproval') {
                const fallbackKey = `processStatus.step.orderApproval.${stepKey}`;
                const fallbackTranslated = t(fallbackKey);
                
                if (fallbackTranslated !== fallbackKey) {
                  return h(UTooltip, { text: fallbackTranslated, 'delay-duration': 0 }, () => 
                    h('div', {
                      class: 'text-sm truncate text-gray-500 dark:text-gray-400 cursor-default'
                    }, fallbackTranslated)
                  );
                }
              }
              
              const displayText = translated !== translationKey ? translated : step;
              
              return h(UTooltip, { text: displayText, 'delay-duration': 0 }, () =>
                h('div', {
                  class: 'text-sm truncate text-gray-500 dark:text-gray-400 cursor-default'
                }, displayText)
              );
            })()
          ])
        ])
      }
    },
    {
      accessorKey: 'expectedDeliveryDate',
      header: () => {
        const isActive = filterValues.value.dateFilterType !== 'all'
        return h('div', { class: 'flex flex-col gap-1.5 w-[140px]' }, [
          h('span', {
            class: ['text-[11px] font-semibold uppercase tracking-wider ml-1.5 transition-colors duration-200', isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400']
          }, t('workflows.table.columns.delivery')),
          /*
          h(UPopover, {
            placement: 'bottom-start',
            class: 'w-full'
          }, {
            default: () => h(UButton, {
              color: isActive ? 'primary' : 'neutral',
              variant: isActive ? 'soft' : 'subtle',
              icon: 'i-lucide-calendar-range',
              size: 'sm',
              class: ['w-full justify-start text-left transition-all duration-300 font-normal shadow-none border-none !px-1.5 !gap-1 overflow-hidden', isActive ? 'ring-2 ring-primary-500/70 bg-primary-50/80 dark:bg-primary-900/30' : 'bg-(--ui-bg) ring-1 ring-(--ui-border) shadow-none']
            }, () => h('span', { class: 'truncate' }, getDateFilterDisplayText())),
            content: () => h('div', { class: 'p-3 space-y-3 min-w-[320px]' }, [
              h('div', { class: 'space-y-2' }, [
                h('div', { class: 'grid grid-cols-2 gap-2' },
                  dateFilterOptions.value.map(option =>
                    h(UButton, {
                      key: option.value,
                      size: 'sm',
                      variant: filterValues.value.dateFilterType === option.value ? 'solid' : 'soft',
                      color: filterValues.value.dateFilterType === option.value ? 'primary' : 'neutral',
                      onClick: () => handleDateFilterTypeChange(option.value),
                      class: 'justify-start'
                    }, () => option.label)
                  )
                )
              ]),
              
              filterValues.value.dateFilterType === 'custom' ? h(USeparator, { class: 'my-3' }) : null,

              filterValues.value.dateFilterType === 'custom' ? h('div', { class: 'space-y-2' }, [
                h('div', { class: 'text-sm font-medium text-(--ui-text) dark:text-gray-300' }, () => t('workflows.table.filters.dateRange')),
                h(UCalendar, {
                  modelValue: filterValues.value.dateRange ?? undefined,
                  'onUpdate:modelValue': (value) => {
                    filterValues.value.dateRange = value
                  },
                  range: true
                })
              ]) : null,

              filterValues.value.dateRange ? h('div', { class: 'pt-2 border-t border-gray-200 dark:border-gray-700' }, [
                h(UButton, {
                  size: 'sm',
                  variant: 'soft',
                  color: 'red',
                  onClick: () => {
                    filterValues.value.dateRange = null
                    filterValues.value.dateFilterType = 'all'
                  },
                  class: 'w-full'
                }, () => t('workflows.table.filters.clearDateFilter'))
              ]) : null
            ])
          })
          */
        ])
      },
      cell: ({ row }) => {
        if (row.original._skeleton) {
          return h('div', {
            class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-20 w-[140px]'
          })
        }
        
        const isChild = !!row.original._isChild;
        
        let displayDate = '';
        
        if (isChild) {
          if (isVehicleOrderType(row.original.type) && row.original.handoverDate) {
            displayDate = row.original.handoverDate;
          } else if (row.original.dealerExpectedDeliveryDate) {
            try {
              displayDate = formatDate(row.original.dealerExpectedDeliveryDate, 'short', locale.value)
            } catch (e) {
              displayDate = '';
            }
          }
        } else {
          displayDate = row.original.handoverDate || row.original.expectedDeliveryDate || '';
        }
        
        return h('span', { class: 'font-medium text-sm text-(--ui-text) w-[140px]' }, displayDate)
      }
    },
    {
      accessorKey: 'supplier',
      header: () => {
        const isActive = !!filterValues.value.supplierName
        return h('div', { class: 'flex flex-col gap-1.5 w-[150px]' }, [
          h('span', { 
            class: ['text-[11px] font-semibold uppercase tracking-wider ml-1.5 transition-colors duration-200', isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400']
          }, t('workflows.table.columns.supplier')),
          h(UInput, {
            modelValue: filterValues.value.supplierName,
            'onUpdate:modelValue': (value) => {
              filterValues.value.supplierName = value || ''
            },
            placeholder: t('workflows.table.filters.supplier'),
            size: 'sm',
            color: isActive ? 'primary' : 'neutral',
            class: 'w-full',
            ui: {
              base: ['transition-all duration-300 shadow-none border-none focus:ring-2 focus:ring-primary-500/50', isActive ? 'ring-2 ring-primary-500/70 bg-primary-50/50 dark:bg-primary-900/30' : 'bg-(--ui-bg) ring-1 ring-(--ui-border) shadow-none'],
              trailing: 'pe-1'
            }
          }, filterTrailingSlot('supplierName'))
        ])
      },
      cell: ({ row }) => {
        if (row.original._skeleton) {
          return h('div', { class: 'flex flex-col gap-2 w-[150px]' }, [
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-28' }),
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-5 rounded-full w-16' })
          ])
        }

        const supplier = row.getValue('supplier')
        const isGroup = row.original._isGroup
        const isChild = row.original._isChild
        
        if (isGroup && row.original._purchaseOrderCount > 1) {
          if (row.original.hasMultipleSuppliers) {
            return h('div', { class: 'flex flex-col w-[150px]' }, [
              h('span', { class: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 ring-1 ring-yellow-300 dark:ring-yellow-700 w-fit' }, t('workflows.table.multipleSuppliers'))
            ])
          }
        }

        if (!supplier) return null

        return h('div', { class: `flex flex-col w-[150px]` }, [
          h(UTooltip, { text: supplier.name, 'delay-duration': 0 }, () =>
            h('span', { class: `font-medium text-sm text-(--ui-text) truncate cursor-default` }, supplier.name)
          ),
          h('span', { class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit mt-1 bg-blue-200 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-700` }, supplier.id)
        ])
      }
    },
    {
      accessorKey: 'client',
      header: () => {
        const isActive = !!filterValues.value.client
        return h('div', { class: 'flex flex-col gap-1.5 w-[150px]' }, [
          h('span', { 
            class: ['text-[11px] font-semibold uppercase tracking-wider ml-1.5 transition-colors duration-200', isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400']
          }, t('workflows.table.columns.client')),
          h(UInput, {
            modelValue: filterValues.value.client,
            'onUpdate:modelValue': (value) => {
              filterValues.value.client = value || ''
            },
            placeholder: t('workflows.table.filters.client'),
            size: 'sm',
            color: isActive ? 'primary' : 'neutral',
            class: 'w-full',
            ui: {
              base: ['transition-all duration-300 shadow-none border-none focus:ring-2 focus:ring-primary-500/50', isActive ? 'ring-2 ring-primary-500/70 bg-primary-50/50 dark:bg-primary-900/30' : 'bg-(--ui-bg) ring-1 ring-(--ui-border) shadow-none'],
              trailing: 'pe-1'
            }
          }, filterTrailingSlot('client'))
        ])
      },
      cell: ({ row }) => {
        if (row.original._skeleton) {
          return h('div', { class: 'flex flex-col gap-2 w-[150px]' }, [
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-32' }),
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-5 rounded-full w-20' })
          ])
        }
        
        const client = row.getValue('client')
        if (!client) return null
        
        return h('div', { class: 'flex flex-col w-[150px]' }, [
          h(UTooltip, { text: client.name, 'delay-duration': 0 }, () =>
            h('span', { class: 'font-medium text-sm text-(--ui-text) truncate cursor-default' }, client.name)
          ),
          h('span', { class: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit mt-1 bg-primary-100 dark:bg-primary-900/30 text-primary-400 dark:text-primary-300 ring-1 ring-primary-200 dark:ring-primary-700' }, client.id)
        ])
      }
    },
    {
      accessorKey: 'description',
      header: () => {
        const isActive = !!filterValues.value.description
        
        return h('div', { class: 'flex flex-col gap-1.5 w-[130px]' }, [
          h('span', {
            class: ['text-[11px] font-semibold uppercase tracking-wider ml-1.5 transition-colors duration-200', isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400']
          }, t('workflows.table.columns.description')),
          h(UInput, {
            modelValue: filterValues.value.description,
            'onUpdate:modelValue': (value) => {
              filterValues.value.description = value || ''
            },
            placeholder: t('workflows.table.filters.description'),
            size: 'sm',
            color: isActive ? 'primary' : 'neutral',
            class: 'w-full',
            ui: {
              base: ['transition-all duration-300 shadow-none border-none focus:ring-2 focus:ring-primary-500/50', isActive ? 'ring-2 ring-primary-500/70 bg-primary-50/50 dark:bg-primary-900/30' : 'bg-(--ui-bg) ring-1 ring-(--ui-border) shadow-none'],
              trailing: 'pe-1'
            }
          }, filterTrailingSlot('description'))
        ])
      },
      cell: ({ row }) => {
        if (row.original._skeleton) {
          return h('div', { class: 'flex flex-col gap-2 w-[130px]' }, [
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-28' })
          ])
        }
        
        const description = row.getValue('description')
        if (!description) return null
        
        return h('div', { class: 'flex flex-col w-[130px]' }, [
          h(UTooltip, { text: description, 'delay-duration': 0 }, () =>
            h('span', { class: 'font-medium text-sm text-(--ui-text) truncate cursor-default' }, description)
          )
        ])
      }
    },
    {
      accessorKey: 'vehicle',
      header: () => {
        const isActive = !!filterValues.value.vehicle
        return h('div', { class: 'flex flex-col gap-1.5 w-[130px]' }, [
          h('span', { 
            class: ['text-[11px] font-semibold uppercase tracking-wider ml-1.5 transition-colors duration-200', isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400']
          }, t('workflows.table.columns.vehicle')),
          h(UInput, {
            modelValue: filterValues.value.vehicle,
            'onUpdate:modelValue': (value) => {
              filterValues.value.vehicle = value || ''
            },
            placeholder: t('workflows.table.filters.vehicle'),
            size: 'sm',
            color: isActive ? 'primary' : 'neutral',
            class: 'w-full',
            ui: {
              base: ['transition-all duration-300 shadow-none border-none focus:ring-2 focus:ring-primary-500/50', isActive ? 'ring-2 ring-primary-500/70 bg-primary-50/50 dark:bg-primary-900/30' : 'bg-(--ui-bg) ring-1 ring-(--ui-border) shadow-none'],
              trailing: 'pe-1'
            }
          }, filterTrailingSlot('vehicle'))
        ])
      },
      cell: ({ row }) => {
        if (row.original._skeleton) {
          return h('div', { class: 'flex flex-col gap-1 w-[130px]' }, [
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-16' }),
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-20' })
          ])
        }
        
        const manufacturer = row.original.manufacturer
        const model = row.original.tenant === Tenant.FI ? (getSapLocalizedText(row.original.modelDescription, locale.value) !== '-' ? getSapLocalizedText(row.original.modelDescription, locale.value) : [row.original.model, row.original.modelTrim].filter(Boolean).join(' ')) : row.original.model
        
        if (!manufacturer && !model) return null
        
        const tooltipText = [manufacturer, model].filter(Boolean).join(' ')
        
        return h(UTooltip, { text: tooltipText, 'delay-duration': 0 }, () =>
          h('div', { class: 'flex flex-col w-[130px] cursor-default' }, [
            manufacturer ? h('span', { class: 'font-medium text-sm text-(--ui-text) truncate' }, manufacturer) : null,
            model ? h('span', { class: 'text-xs text-gray-500 dark:text-gray-400 truncate' }, model) : null
          ])
        )
      }
    },
    {
      accessorKey: 'assignees',
      header: () => h('div', { class: 'flex flex-col gap-1.5 w-[120px]' }, [
        h('span', { class: 'text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider ml-1.5' }, t('workflows.table.columns.assignees')),
        h('div', { class: 'h-8' })
      ]),
      cell: ({ row }) => {
        if (row.original._skeleton) {
          return h('div', { class: 'flex items-center gap-1 w-[120px]' }, [
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) w-7 h-7 rounded-full' }),
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) w-7 h-7 rounded-full' }),
            h('div', { class: 'animate-pulse bg-(--ui-bg-accented) w-7 h-7 rounded-full' })
          ])
        }
        
        const assignees = row.getValue('assignees')
        
        const items = (assignees || []).map((assignee: any) => {
          let label = ''
          let icon = ''
          let initials = ''

          if (assignee.role) {
            label = assignee.role
            icon = 'i-heroicons-users'
            
            const segments = String(label).trim().split(/[ _\-]+/).filter(Boolean)
            initials = segments.map(s => s[0]).join('').slice(0, 2).toUpperCase()
          } else if (assignee.user) {
            label = assignee.user.fullName
            
            const parts = String(label).trim().split(/\s+/).filter(Boolean)
            const first = parts[0]?.[0] || ''
            const second = parts.length > 1 ? parts[1]?.[0] : (parts[0]?.[1] || '')
            initials = (first + (second || '')).toUpperCase()
          } else if (assignee.group) {
            label = assignee.group
            
            const segments = String(label).trim().split(/[ _\-]+/).filter(Boolean)
            initials = segments.map(s => s[0]).join('').slice(0, 2).toUpperCase()
          } else {
            label = assignee.email
            icon = 'i-heroicons-envelope'
          }

          return { label, icon, initials }
        })
        
        const maxVisible = 3
        const hiddenCount = Math.max(0, items.length - maxVisible)
        
        return h('div', { class: 'flex items-center gap-1 whitespace-nowrap overflow-hidden w-[120px]' }, [
          ...items.slice(0, maxVisible).map((item: any) =>
            h(UTooltip, { text: item.label, 'delay-duration': 0 }, () =>
              h('span', { class: 'inline-flex items-center justify-center rounded-full bg-(--ui-bg-elevated) w-7 h-7 cursor-pointer' }, [
                item.initials ? h('span', { class: 'text-sm font-medium text-gray-700 dark:text-gray-200 leading-none' }, item.initials) : h(UIcon, { name: item.icon, class: 'w-5 h-5 text-gray-700 dark:text-gray-200' })
              ])
            )
          ),
          hiddenCount > 0
            ? h(UTooltip, { text: items.slice(maxVisible).map((i: any) => i.label).join(', '), 'delay-duration': 0 }, () =>
                h('span', { class: 'inline-flex items-center justify-center rounded-full bg-(--ui-bg-elevated) text-sm text-gray-700 dark:text-gray-200 w-7 h-7 cursor-pointer' }, `+${hiddenCount}`)
              )
            : null
        ])
      }
    }
  ]
  
  return baseColumns.filter(col => col.accessorKey !== 'description' || tenant.value === Tenant.FI)
})

const handleAssignedFilterChange = (assignedValue: boolean) => {
  filterValues.value.assigned = assignedValue
}

const debouncedApplyFilters = debounce(async (filters: any) => {
  await applyFilters(filters)
}, 400)

watch(data, (newData) => {
  if (newData && newData.length > 0) {
    initializeExpandedRows()
  }
}, { deep: true, immediate: true })

onMounted(async () => {
  await fetchWorkflows(selectedPageSize.value, 1, { assigned: filterValues.value.assigned })

  initializeExpandedRows()
  
  const modalExternalReference = route.query.modalExternalReference
  const modalType = route.query.modalType

  if (modalExternalReference) {
    selectedExternalReference.value = modalExternalReference as string
    selectedPurchaseOrderType.value = modalType as string | undefined
    isModalOpen.value = true
  }
})

watch(() => viewStore.currentView, async (newView, oldView) => {
  if (newView !== oldView) {
    try {
      currentPaginationPage.value = 1
      await fetchWorkflows(selectedPageSize.value, 1)
    } catch (error) {
      showError(error instanceof Error ? error.message : String(error))
    }
  }
}, { immediate: false })

watch(filterValues, (newFilters) => {
  const apiFilters: any = {}
  
  const query = { ...route.query }
  
  if (newFilters.assigned) {
    query.assigned = 'true'
  } else {
    delete query.assigned
  }
  
  if (route.query.assigned !== query.assigned) {
    router.replace({ query }).catch(() => {})
  }
  
  if (newFilters.externalReference && newFilters.externalReference.trim() !== '') {
    apiFilters.externalReference = [newFilters.externalReference.trim()]
  }

  if (newFilters.client && newFilters.client.trim() !== '') {
    apiFilters.client = newFilters.client.trim()
  }
  
  if (newFilters.description && newFilters.description.trim() !== '') {
    apiFilters.description = newFilters.description.trim()
  }
  
  if (newFilters.supplierName && newFilters.supplierName.trim() !== '') {
    apiFilters.supplierName = newFilters.supplierName.trim()
  }
  
  if (newFilters.vehicle && newFilters.vehicle.trim() !== '') {
    apiFilters.vehicle = newFilters.vehicle.trim()
  }
  
  if (newFilters.statusStep && Array.isArray(newFilters.statusStep) && newFilters.statusStep.length > 0) {
    const statuses = new Set<string>()
    const statusStepCombinations: any[] = []

    const allApprovalSteps = [QuotationStep.DriverApproval, QuotationStep.HRApproval, QuotationStep.FleetManagerApproval, QuotationStep.PurchaseSupportApproval, QuotationStep.SupervisorApproval, QuotationStep.NotRegisteredUserApproval, QuotationStep.GroupApproval, QuotationStep.PersonApproval]
    
    newFilters.statusStep.forEach(item => {
      const itemStr = String(item)
      
      if (itemStr.startsWith('status:')) {
        const status = itemStr.replace('status:', '')
        statuses.add(status)
      } else if (itemStr.startsWith('status-step:')) {
        const [, status, step] = itemStr.split(':')
        statuses.add(status)
        
        if (step === 'Approvals') {
          allApprovalSteps.forEach(approvalStep => {
            statusStepCombinations.push({ status, step: approvalStep })
          })
        } else {
          statusStepCombinations.push({ status, step })
        }
      }
    })

    if (statuses.size > 0) {
      apiFilters.status = Array.from(statuses)
    }

    if (statusStepCombinations.length > 0) {
      apiFilters.statusStep = statusStepCombinations
    }
  }

  if (newFilters.dateFilter && newFilters.dateFilter !== 'all') {
    apiFilters.dateFilter = newFilters.dateFilter
  }

  if (newFilters.assignee && newFilters.assignee.trim() !== '') {
    apiFilters.assignee = newFilters.assignee.trim()
  }

  if (newFilters.assigned !== null) {
    apiFilters.assigned = newFilters.assigned
  }

  debouncedApplyFilters(apiFilters)
}, { deep: true })
</script>